import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  PlusIcon,
  TrashIcon,
  SwatchIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const PRESET_COLORS = [
  '#dcfce7', '#d1fae5', '#cffafe', '#dbeafe', '#e0e7ff',
  '#ede9fe', '#fce7f3', '#ffe4e6', '#fef3c7', '#ffedd5',
  '#fee2e2', '#f3f4f6',
];

const PRESET_TEXT_COLORS = [
  '#166534', '#065f46', '#155e75', '#1e40af', '#3730a3',
  '#6b21a8', '#9d174d', '#9f1239', '#92400e', '#9a3412',
  '#991b1b', '#374151', '#ffffff', '#000000',
];

const OPERATORS = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: 'greater or equal' },
  { value: '<=', label: 'less or equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

function ColorPicker({ color, onChange, presets, label }) {
  const [showPicker, setShowPicker] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const panelWidth = 176; // w-44 = 11rem = 176px
    const panelHeight = 200;
    let top = rect.bottom + 4;
    let left = rect.left;

    if (left + panelWidth > window.innerWidth - 8) {
      left = window.innerWidth - panelWidth - 8;
    }
    if (top + panelHeight > window.innerHeight - 8) {
      top = rect.top - panelHeight - 4;
    }
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (!showPicker) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showPicker, updatePosition]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setShowPicker(!showPicker)}
        className="flex items-center gap-1.5 px-2 py-1 rounded border border-surface-200 dark:border-surface-700 hover:border-surface-400 dark:hover:border-surface-500 transition-colors text-xs"
      >
        <span
          className="w-4 h-4 rounded border border-surface-300 dark:border-surface-600 flex-shrink-0"
          style={{ backgroundColor: color || '#f3f4f6' }}
        />
        <span className="text-surface-600 dark:text-surface-400 truncate max-w-[60px]">
          {color || label || 'Pick'}
        </span>
      </button>

      {showPicker && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setShowPicker(false)} />
          <div
            ref={panelRef}
            className="fixed z-[9999] bg-white dark:bg-surface-800 rounded-lg shadow-xl border border-surface-200 dark:border-surface-700 p-2 w-44"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="grid grid-cols-6 gap-1 mb-2">
              {presets.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { onChange(c); setShowPicker(false); }}
                  className={`w-6 h-6 rounded border transition-all ${
                    color === c
                      ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800 scale-110'
                      : 'border-surface-200 dark:border-surface-600 hover:scale-110'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="color"
                value={color || '#f3f4f6'}
                onChange={(e) => onChange(e.target.value)}
                className="w-6 h-6 rounded border border-surface-200 dark:border-surface-600 cursor-pointer"
              />
              <input
                type="text"
                value={color || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#hex"
                className="flex-1 text-xs px-2 py-1 rounded border border-surface-200 dark:border-surface-700 bg-transparent text-surface-700 dark:text-surface-300"
              />
            </div>
            {color && (
              <button
                type="button"
                onClick={() => { onChange(''); setShowPicker(false); }}
                className="w-full mt-1.5 text-xs text-surface-500 hover:text-red-500 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function RuleRow({ rule, columns, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(true);
  const hasCondition = !!rule.condition?.column;
  const noValueOps = ['is_empty', 'is_not_empty'];

  const updateField = (field, value) => {
    onChange({ ...rule, [field]: value });
  };

  const updateCondition = (field, value) => {
    const cond = { ...(rule.condition || {}), [field]: value };
    onChange({ ...rule, condition: cond });
  };

  const toggleCondition = () => {
    if (hasCondition) {
      onChange({ ...rule, condition: null });
    } else {
      onChange({ ...rule, condition: { column: columns[0] || '', operator: '=', value: '' } });
    }
  };

  return (
    <div className="border border-surface-200 dark:border-surface-700 rounded-lg bg-white dark:bg-surface-800 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setExpanded(!expanded)} className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-300">
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span
            className="w-3 h-3 rounded-sm flex-shrink-0 border border-surface-300 dark:border-surface-600"
            style={{ backgroundColor: rule.backgroundColor || '#f3f4f6' }}
          />
          <span className="text-xs text-surface-700 dark:text-surface-300 truncate">
            {hasCondition ? (
              <>When <strong>{rule.condition.column}</strong> {rule.condition.operator} {noValueOps.includes(rule.condition.operator) ? '' : `"${rule.condition.value}"`}</>
            ) : rule.target === 'column' ? (
              <>Column: <strong>{rule.column || '—'}</strong></>
            ) : (
              <>Row: <strong>{rule.rowIndex != null ? rule.rowIndex + 1 : '—'}</strong></>
            )}
          </span>
        </div>

        <button type="button" onClick={onRemove} className="p-1 text-surface-400 hover:text-red-500 transition-colors">
          <TrashIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-surface-100 dark:border-surface-700 pt-2.5">
          {/* Target: row or column */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-surface-500 dark:text-surface-400 w-14 flex-shrink-0">Apply to</label>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => updateField('target', 'row')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  rule.target === 'row'
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                    : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'
                }`}
              >
                Row
              </button>
              <button
                type="button"
                onClick={() => updateField('target', 'column')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  rule.target === 'column'
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                    : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'
                }`}
              >
                Column
              </button>
            </div>
          </div>

          {/* Manual selection (when no condition) */}
          {!hasCondition && rule.target === 'column' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-surface-500 dark:text-surface-400 w-14 flex-shrink-0">Column</label>
              <select
                value={rule.column || ''}
                onChange={(e) => updateField('column', e.target.value)}
                className="flex-1 text-xs px-2 py-1.5 rounded border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-300"
              >
                <option value="">Select column...</option>
                {columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          )}

          {!hasCondition && rule.target === 'row' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-surface-500 dark:text-surface-400 w-14 flex-shrink-0">Row #</label>
              <input
                type="number"
                min={1}
                value={rule.rowIndex != null ? rule.rowIndex + 1 : ''}
                onChange={(e) => updateField('rowIndex', e.target.value ? parseInt(e.target.value, 10) - 1 : null)}
                placeholder="Row number"
                className="flex-1 text-xs px-2 py-1.5 rounded border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-300"
              />
            </div>
          )}

          {/* Toggle conditional mode */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-surface-500 dark:text-surface-400 w-14 flex-shrink-0">Logic</label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={hasCondition}
                onChange={toggleCondition}
                className="rounded border-surface-300 dark:border-surface-600 text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
              />
              <span className="text-xs text-surface-600 dark:text-surface-400">Use conditional logic</span>
            </label>
          </div>

          {/* Condition fields */}
          {hasCondition && (
            <div className="pl-[3.75rem] space-y-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-surface-500 dark:text-surface-400 flex-shrink-0">When</span>
                <select
                  value={rule.condition?.column || ''}
                  onChange={(e) => updateCondition('column', e.target.value)}
                  className="flex-1 text-xs px-2 py-1.5 rounded border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-300 min-w-0"
                >
                  <option value="">Column...</option>
                  {columns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <select
                  value={rule.condition?.operator || '='}
                  onChange={(e) => updateCondition('operator', e.target.value)}
                  className="flex-1 text-xs px-2 py-1.5 rounded border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-300"
                >
                  {OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                {!noValueOps.includes(rule.condition?.operator) && (
                  <input
                    type="text"
                    value={rule.condition?.value || ''}
                    onChange={(e) => updateCondition('value', e.target.value)}
                    placeholder="Value..."
                    className="flex-1 text-xs px-2 py-1.5 rounded border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-300"
                  />
                )}
              </div>
            </div>
          )}

          {/* Colors */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-surface-500 dark:text-surface-400 w-14 flex-shrink-0">Colors</label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs text-surface-400">BG</span>
                <ColorPicker
                  color={rule.backgroundColor}
                  onChange={(c) => updateField('backgroundColor', c)}
                  presets={PRESET_COLORS}
                  label="Background"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-surface-400">Text</span>
                <ColorPicker
                  color={rule.textColor}
                  onChange={(c) => updateField('textColor', c)}
                  presets={PRESET_TEXT_COLORS}
                  label="Text"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TableColorConfigurator({ colorRules = [], columns = [], onChange }) {
  const addRule = (target) => {
    const newRule = {
      id: `rule_${Date.now()}`,
      target,
      column: target === 'column' ? (columns[0] || '') : null,
      rowIndex: target === 'row' ? 0 : null,
      condition: null,
      backgroundColor: target === 'column' ? '#dbeafe' : '#dcfce7',
      textColor: '',
    };
    onChange([...colorRules, newRule]);
  };

  const updateRule = (index, updated) => {
    const next = [...colorRules];
    next[index] = updated;
    onChange(next);
  };

  const removeRule = (index) => {
    onChange(colorRules.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {colorRules.length === 0 ? (
        <div className="text-center py-4">
          <SwatchIcon className="w-8 h-8 text-surface-300 dark:text-surface-600 mx-auto mb-2" />
          <p className="text-xs text-surface-500 dark:text-surface-400 mb-3">
            No color rules yet. Add rules to highlight rows or columns.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {colorRules.map((rule, i) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              columns={columns}
              onChange={(updated) => updateRule(i, updated)}
              onRemove={() => removeRule(i)}
            />
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => addRule('column')}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-lg transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Color Column
        </button>
        <button
          type="button"
          onClick={() => addRule('row')}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-accent-900/30 hover:bg-accent-100 dark:hover:bg-accent-900/50 rounded-lg transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Color Row
        </button>
      </div>
    </div>
  );
}
